<script setup lang="ts">
import type { Album } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { useDeleteAlbum } from "@/api"
import { BaseModal, Button, Icon, Input } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { AlbumDeleteModalKey } from "@/symbols"
import { useToast } from "../ui/ToastReka/useToast"

interface AlbumDeleteModalProps {
  album?: Album
  isLoading?: boolean
}

const props = withDefaults(defineProps<AlbumDeleteModalProps>(), { isLoading: true })

const emit = defineEmits<{ success: [], error: [] }>()

const eventStore = useEventVaultStore()

const { t } = useTranslation()

const { toggle } = useModal(AlbumDeleteModalKey)

const { success, error } = useToast()

const deleteConfirmation = ref<string>("")

const client = useQueryClient()

const { mutate, isPending } = useDeleteAlbum()

async function doDeleteAlbum() {
  if (!eventStore.eventVaultId || !props.album?.id) {
    error({
      title: t("album_delete_modal.missing_data_title"),
      description: t("album_delete_modal.missing_data_desc"),
    })
    return
  }

  if (deleteConfirmation.value !== props.album?.title) {
    return
  }

  mutate({ eventVaultId: eventStore.eventVaultId, albumId: props.album.id }, {
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["listAlbums", eventStore.eventVaultId] })
      await client.invalidateQueries({ queryKey: ["getEventVault", eventStore.eventVaultId] })

      deleteConfirmation.value = ""
      toggle()

      success({ description: t("album_delete_modal.success"), title: t("success"), icon: "CircleCheck" })

      emit("success")
    },
    onError: () => {
      error()
      emit("error")
    },
  })
}

const deleteConfirmationValidation = computed(() => {
  if (deleteConfirmation.value && deleteConfirmation.value !== props.album?.title)
    return t("album_delete_modal.wrong_name_validation")

  return ""
})

const disableDelete = computed(() =>
  !deleteConfirmation.value
  || Boolean(deleteConfirmationValidation.value)
  || isPending.value,
)

function onClose() {
  deleteConfirmation.value = ""
  toggle()
}
</script>

<template>
  <BaseModal :name="AlbumDeleteModalKey" data-test="delete-album-modal">
    <div class="bg-white rounded-lg overflow-hidden flex flex-col">
      <div class="shrink-0 bg-white p-6 pb-4 border-b border-gray-200">
        <div class="flex items-start justify-between">
          <div>
            <h1 class="font-heading font-semibold text-lg leading-tight md:text-xl flex gap-2">
              <Icon icon="Caution" class="shrink-0" />
              {{ t("album_delete_modal.title") }}
            </h1>
            <p class="text-sm text-label hidden md:block font-body ml-8">
              {{ t("album_delete_modal.description") }}
            </p>
          </div>
          <Button variant="flat" prefix-icon="Close" class="p-0" data-test="close-button" @click="onClose">
            <span class="sr-only">{{ t("album_delete_modal.close") }}</span>
          </Button>
        </div>
      </div>

      <form class="p-6 pt-4 flex flex-col gap-4 text-sm md:text-base" @submit.prevent="doDeleteAlbum">
        <div v-if="isLoading" class="gpx-skeleton h-24" />
        <template v-else>
          <div v-if="album?.totalPhotos || album?.totalVideos || album?.zipSummary?.downloadUrl" class="flex flex-col gap-2" data-test="album-content-summary">
            <p>{{ t("album_delete_modal.message") }}</p>
            <ul class="list-disc pl-5 flex flex-col gap-1">
              <li v-if="album?.totalPhotos && album.totalPhotos > 0">
                {{ t("album_delete_modal.photo_count", album.totalPhotos) }}
              </li>
              <li v-if="album?.totalVideos && album.totalVideos > 0">
                {{ t("album_delete_modal.video_count", album.totalVideos) }}
              </li>
              <li v-if="album?.zipSummary?.downloadUrl">
                {{ t("album_delete_modal.zip_count") }}
              </li>
            </ul>
          </div>

          <p>{{ t("album_delete_modal.warning") }}</p>

          <p>
            <i18n-t keypath="album_delete_modal.confirm_message" scope="global">
              <template #albumTitle>
                <strong>{{ album?.title }}</strong>
              </template>
            </i18n-t>
          </p>

          <Input
            v-model="deleteConfirmation"
            name="deleteConfirmation"
            data-test="confirmation-input"
            :label="t('album_delete_modal.confirm_label')"
            :placeholder="album?.title"
            :error="deleteConfirmationValidation"
          />
        </template>
      </form>

      <div class="shrink-0 bg-white border-t border-gray-200 p-6 pt-4">
        <div class="flex justify-end gap-3">
          <Button variant="flat" :disabled="isPending" data-test="cancel-button" @click="onClose">
            {{ t("album_delete_modal.cancel") }}
          </Button>
          <Button variant="danger" :disabled="disableDelete" data-test="delete-button" @click="doDeleteAlbum">
            <span class="hidden md:block">{{ isPending ? t("album_delete_modal.deleting") : t("album_delete_modal.confirm") }}</span>
            <span class="block md:hidden">{{ isPending ? t("album_delete_modal.deleting") : t("album_delete_modal.confirm_short") }}</span>
            <template #suffixIcon>
              <Icon v-if="isPending" icon="Loading" />
            </template>
          </Button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
